import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronDown,
  Download,
  FileSignature,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { formatVidimVerifica, normalizeBlocchi, type VidimBlocco } from "@/lib/vidimazione";
import { cn } from "@/lib/utils";

function newIdemKey() {
  return crypto.randomUUID?.() ?? `idem-${Date.now()}`;
}

export function Vidimazione() {
  const ctx = useOperatoreContext();
  const qc = useQueryClient();
  const [num, setNum] = useState("");
  const [active, setActive] = useState<string | null>(null);
  const [richiediBlocco, setRichiediBlocco] = useState<VidimBlocco | null>(null);
  const [dettaglioOpen, setDettaglioOpen] = useState(false);
  const [dettaglio, setDettaglio] = useState({ codice: "", progressivo: "" });

  const blocchi = useQuery({
    queryKey: ["vidim-blocchi"],
    queryFn: endpoints.vidimazione.lista,
  });

  const rows = useMemo(() => normalizeBlocchi(blocchi.data), [blocchi.data]);

  const verifyQ = useQuery({
    queryKey: ["vidim-verify", active],
    queryFn: () => endpoints.vidimazione.verifica(active!),
    enabled: !!active,
  });

  const verificaFmt = verifyQ.data ? formatVidimVerifica(verifyQ.data) : null;

  const richiediMut = useMutation({
    mutationFn: (codice: string) =>
      endpoints.vidimazione.richiedi(
        codice,
        ctx.numIscrSito ? { num_iscr_sito: ctx.numIscrSito } : {},
        newIdemKey(),
      ),
    onSuccess: (r) => {
      toast.success(`Richiesta vidimazione inviata (${r.rentri_status})`, {
        description: "Controlla Job RENTRI per l'esito.",
      });
      setRichiediBlocco(null);
      qc.invalidateQueries({ queryKey: ["vidim-blocchi"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Vidimazione fallita"),
  });

  const dettaglioQ = useQuery({
    queryKey: ["vidim-det", dettaglio.codice, dettaglio.progressivo],
    queryFn: () =>
      endpoints.vidimazione.dettaglio(
        dettaglio.codice,
        parseInt(dettaglio.progressivo, 10),
      ),
    enabled: false,
  });

  const runVerify = () => {
    const n = num.trim().replace(/\s+/g, "");
    setActive(n || null);
  };

  const pdfMut = useMutation({
    mutationFn: ({ codice, progressivo }: { codice: string; progressivo: number }) =>
      endpoints.vidimazione.pdf(codice, progressivo, `fir-vidimato-${codice}-${progressivo}.pdf`),
    onSuccess: () => toast.success("PDF scaricato"),
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Download PDF fallito"),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Vidimazione FIR</h1>
          <p className="text-sm text-muted-foreground">
            Formulari cartacei — richiesta numero FIR e verifica stato
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => blocchi.refetch()}
          disabled={blocchi.isFetching}
        >
          <RefreshCw className={cn("mr-2 h-4 w-4", blocchi.isFetching && "animate-spin")} />
          Aggiorna blocchi
        </Button>
      </div>

      {!ctx.numIscrSito && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="p-4 text-sm">
            Imposta l&apos;unità locale nella barra laterale per vidimare FIR associati al sito.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> Blocchi disponibili
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blocchi.isLoading && <CenteredLoader />}
          {blocchi.error && <ErrorState error={blocchi.error} />}
          {!blocchi.isLoading && !blocchi.error && rows.length === 0 && (
            <EmptyState
              title="Nessun blocco disponibile"
              description="I blocchi vengono assegnati da RENTRI all'operatore"
            />
          )}
          {rows.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Codice blocco</TH>
                  <TH>Descrizione</TH>
                  <TH className="text-right">Disponibili</TH>
                  <TH className="text-right">Azioni</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((b) => (
                  <TR key={b.codice}>
                    <TD className="font-mono text-sm">{b.codice}</TD>
                    <TD className="text-sm text-muted-foreground">
                      {b.descrizione || "—"}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {b.disponibili != null ? b.disponibili : "—"}
                    </TD>
                    <TD className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRichiediBlocco(b)}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Richiedi FIR
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Verifica numero FIR</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-medium text-muted-foreground">Numero FIR</label>
              <Input
                className="mt-1 font-mono"
                placeholder="Es. 1234567890123456789012345"
                value={num}
                onChange={(e) => setNum(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runVerify()}
              />
            </div>
            <Button onClick={runVerify} disabled={!num.trim()}>
              <Search className="mr-2 h-4 w-4" /> Verifica
            </Button>
          </div>
          {active && (
            <>
              {verifyQ.isLoading && <CenteredLoader />}
              {verifyQ.error && <ErrorState error={verifyQ.error} />}
              {verificaFmt && verificaFmt.lines.length > 0 && (
                <div className="rounded-md border p-4 space-y-2">
                  <Badge variant={verificaFmt.ok ? "success" : "secondary"}>
                    {verificaFmt.ok ? "Trovato" : "Verifica"}
                  </Badge>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {verificaFmt.lines.map((l) => (
                      <div key={l.label}>
                        <dt className="text-xs text-muted-foreground">{l.label}</dt>
                        <dd className="font-mono">{l.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {verificaFmt && verificaFmt.lines.length === 0 && verifyQ.data != null && (
                <p className="text-sm text-muted-foreground">Nessun dettaglio strutturato nella risposta.</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <button
          type="button"
          className="flex w-full items-center justify-between px-6 py-4 text-left"
          onClick={() => setDettaglioOpen((v) => !v)}
        >
          <span className="text-base font-medium">Dettaglio / PDF vidimazione</span>
          <ChevronDown
            className={cn("h-5 w-5 transition", dettaglioOpen && "rotate-180")}
          />
        </button>
        {dettaglioOpen && (
          <CardContent className="border-t space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Codice blocco</label>
                <Input
                  className="mt-1 font-mono"
                  value={dettaglio.codice}
                  onChange={(e) => setDettaglio((d) => ({ ...d, codice: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Progressivo</label>
                <Input
                  className="mt-1"
                  type="number"
                  min={1}
                  value={dettaglio.progressivo}
                  onChange={(e) => setDettaglio((d) => ({ ...d, progressivo: e.target.value }))}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  variant="outline"
                  disabled={
                    !dettaglio.codice ||
                    !dettaglio.progressivo ||
                    dettaglioQ.isFetching
                  }
                  onClick={() => dettaglioQ.refetch()}
                >
                  Carica dettaglio
                </Button>
                <Button
                  variant="outline"
                  disabled={
                    !dettaglio.codice ||
                    !dettaglio.progressivo ||
                    pdfMut.isPending
                  }
                  onClick={() =>
                    pdfMut.mutate({
                      codice: dettaglio.codice,
                      progressivo: parseInt(dettaglio.progressivo, 10),
                    })
                  }
                >
                  <Download className="mr-2 h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
            {dettaglioQ.isLoading && <CenteredLoader />}
            {dettaglioQ.error && <ErrorState error={dettaglioQ.error} />}
            {dettaglioQ.data != null && (
              <pre className="overflow-x-auto rounded bg-muted/50 p-3 text-xs max-h-64">
                {JSON.stringify(dettaglioQ.data, null, 2)}
              </pre>
            )}
          </CardContent>
        )}
      </Card>

      <Dialog open={!!richiediBlocco} onOpenChange={(o) => !o && setRichiediBlocco(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Richiedi vidimazione FIR</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Blocco <span className="font-mono font-medium">{richiediBlocco?.codice}</span>
            {ctx.numIscrSito && (
              <>
                {" "}
                — unità locale <span className="font-mono">{ctx.numIscrSito}</span>
              </>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            L&apos;operazione è asincrona: al termine controlla Job RENTRI.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRichiediBlocco(null)}>
              Annulla
            </Button>
            <Button
              onClick={() => richiediBlocco && richiediMut.mutate(richiediBlocco.codice)}
              disabled={richiediMut.isPending}
            >
              {richiediMut.isPending ? "Invio…" : "Conferma richiesta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
